import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { logger } from '../services/loggerService';

const STORAGE_KEY = 'viewModeState';

export const useViewModeStore = defineStore('viewMode', () => {
  const isImpersonating = ref(false);
  const impersonatedCompany = ref(null);
  
  // userAuthStore is loaded lazily to avoid a circular dependency
  // (userAuthStore imports this store statically)
  const _useUserAuthStore = ref(null);
  import('./userAuthStore').then((m) => {
    _useUserAuthStore.value = m.useUserAuthStore;
  }).catch((e) => {
    logger.error('[ViewMode] Failed to load userAuthStore', { error: e.message });
  });

  const isNexusMode = computed(() => {
    // Only admin/super users can be in Nexus mode, and only when not impersonating
    if (_useUserAuthStore.value) {
      const userType = _useUserAuthStore.value().user?.type;
      return (userType === 'admin' || userType === 'super') && !isImpersonating.value;
    }
    // Fallback until the lazy import resolves (first microtasks after store creation)
    return !isImpersonating.value;
  });

  // Try to restore state from localStorage
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      const { isImpersonating: savedImpersonating, impersonatedCompany: savedCompany } = JSON.parse(savedState);
      isImpersonating.value = savedImpersonating;
      impersonatedCompany.value = savedCompany;
    }
  } catch (e) {
    logger.error('[ViewMode] Failed to restore view mode state:', { error: e.message });
    // Reset to default state
    isImpersonating.value = false;
    impersonatedCompany.value = null;
  }

  function startImpersonation(companyId) {
    isImpersonating.value = true;
    impersonatedCompany.value = companyId;
    saveState();
    logger.info('[ViewMode] Started company impersonation', { companyId });
  }

  function stopImpersonation() {
    isImpersonating.value = false;
    impersonatedCompany.value = null;
    saveState();
    logger.info('[ViewMode] Stopped company impersonation');
  }

  function saveState() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      isImpersonating: isImpersonating.value,
      impersonatedCompany: impersonatedCompany.value
    }));
  }

  return {
    isImpersonating,
    impersonatedCompany,
    isNexusMode,
    startImpersonation,
    stopImpersonation
  };
}); 